import { InputBaseProps, Paper } from "@mui/material";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/react";
import { ReactNode } from "react";
import { QueryKey } from "@tanstack/react-query";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";
import { useGet, UseGetOptions } from "@/hooks";

export type FormAsyncSelectValue = null | number | string;

export type FormAsyncSelectProps<TFormValues extends FieldValues, TData> = Partial<
  Omit<
    AutocompleteProps<TData, false, false, false>,
    "name" | "disableClearable" | "freeSolo" | "control" | "options" | "defaultValue"
  >
> & {
  name: Path<TFormValues>;
  getOptionLabel: (data: TData) => string;
  getOptionSecondaryLabel?: (data: TData) => string;
  getOptionIcon?: (data: TData) => ReactNode;
  getOptionValue: (data: TData) => number | string;
  getFilteredOptions?: (data: TData[], values: TFormValues) => TData[];
  sortBy?: (a: TData, b: TData) => number;
  endpoint: string;
  queryKey?: QueryKey;
  required?: TextFieldProps["required"];
  label?: TextFieldProps["label"];
  helperText?: TextFieldProps["helperText"];
  getOptions?: UseGetOptions<TData[]>;
  inputSlotProps?: InputBaseProps;
};

export default function FormikAsyncSelect<TFormValues extends FieldValues, TData>({
  name,
  getOptionLabel,
  getOptionSecondaryLabel = () => "",
  getOptionIcon = () => null,
  getOptionValue,
  getFilteredOptions = (options) => options,
  sortBy,
  endpoint = "",
  queryKey = [endpoint],
  label = "",
  required = false,
  disabled = false,
  getOptions = {},
  ...props
}: FormAsyncSelectProps<TFormValues, TData>) {
  const { control, setValue, watch } = useFormContext<TFormValues>();
  const values = watch();

  const { data = [], isLoading } = useGet<TData[]>({
    endpoint,
    queryKey,
    options: getOptions,
  });

  const sortedData = sortBy ? data.sort(sortBy) : data;

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <Autocomplete<TData, false, false, false>
              value={sortedData.find((item) => getOptionValue(item) === field.value) ?? null}
              options={getFilteredOptions(sortedData, values)}
              loading={isLoading}
              getOptionLabel={(option) => getOptionLabel(option) ?? ""}
              disabled={disabled}
              fullWidth
              onChange={(event, value) => {
                setValue(
                  field.name,
                  (value ? getOptionValue(value) : null) as PathValue<TFormValues, Path<TFormValues>>
                );
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    name={field.name}
                    label={label}
                    required={required}
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off",
                    }}
                  />
                );
              }}
              PaperComponent={(paperProps) => {
                const { children, ...restPaperProps } = paperProps;
                return <Paper {...restPaperProps}>{children}</Paper>;
              }}
              renderOption={(props, option) => {
                return (
                  <Stack
                    component="li"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between !important"
                    spacing={2}
                    {...props}
                    key={props.id}
                  >
                    <Stack
                      sx={{ "& svg": { color: "primary.main", fontSize: 20 } }}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      {getOptionIcon(option)}
                      <Stack direction="column">
                        <Typography variant="body1">{getOptionLabel(option)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getOptionSecondaryLabel(option)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                );
              }}
              {...props}
            />
          );
        }}
      />
    </Sentry.ErrorBoundary>
  );
}
